import { Play } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useStartProject } from '@/features/doer/use-doer'
import { canonicalProjectUrl } from '@/lib/canonical-url'

interface StartProjectButtonProps {
  projectId: string
  authorHandle: string
  slug: string
}

/**
 * "Start this project" CTA on the viewer. Shown to any signed-in user without a Following — that
 * includes the project's own author when they've opened {@code ?mode=view}. After a successful
 * start, replace the URL with the bare canonical URL (no {@code ?mode=}) so the doing-view branch
 * wins and the user lands in their new build.
 */
export const StartProjectButton = ({ projectId, authorHandle, slug }: StartProjectButtonProps) => {
  const navigate = useNavigate()
  const start = useStartProject()

  const handleStart = (): void => {
    start.mutate(projectId, {
      onSuccess: () => {
        toast.success('Project started')
        navigate(canonicalProjectUrl(authorHandle, slug), { replace: true })
      },
      onError: (err) => {
        const apiErr = err as { status?: number; detail?: string }
        toast.error(apiErr.detail ?? 'Could not start this project')
      },
    })
  }

  return (
    <Button onClick={handleStart} disabled={start.isPending} className="gap-2">
      <Play className="h-4 w-4" />
      {start.isPending ? 'Starting…' : 'Start this project'}
    </Button>
  )
}
